import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import toConnectedMapNode from 'common/src/modules/campaigns/store/campaignLogic/utils/toConnectedMapNode';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../../campaignSetupAgent.enums';
import {cardToText} from '../textualConverters';

const getCardDetailsFromReduxToolSchema = z.object({
  nodeIds: z.array(z.number()).describe('The IDs of the cards to get details for'),
});

function formatList(items: number[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return String(items[0]);
  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  const last = items[items.length - 1];
  const rest = items.slice(0, -1).join(', ');

  return `${rest} and ${last}`;
}

export function getCardDetailsFromReduxTool(store: CLStore) {
  async function execute({
    input: {nodeIds},
  }: ToolExecuteParams<typeof getCardDetailsFromReduxToolSchema>): Promise<ToolResult> {
    try {
      const state = store.getState();
      const {campaignLogic} = state.campaignPage;

      const nodes = campaignLogic.nodes[CampaignTreeType.Campaign];

      const existingNodes = nodeIds.map((nodeId) => nodes[nodeId]).filter(Boolean);

      const textResults = existingNodes.map((node) => {
        const connectedNode = toConnectedMapNode(node);
        const text = cardToText(connectedNode, 'full');

        return {node, text};
      });

      const cardTexts = textResults
        .filter((result): result is {node: typeof result.node; text: string} => result.text != null)
        .map((result) => result.text);

      const unsupportedCardIds = textResults
        .filter((result) => result.text == null)
        .map((result) => result.node.id);

      const missingNodesMessage =
        existingNodes.length !== nodeIds.length
          ? `Some cards were not found: ${JSON.stringify(
              nodeIds.filter((nodeId) => !existingNodes.some((node) => node.id === nodeId)),
            )}`
          : '';

      const unsupportedMessage =
        unsupportedCardIds.length === 1
          ? `Card ${unsupportedCardIds[0]} is of a card type that is currently not supported.`
          : unsupportedCardIds.length > 1
            ? `Cards ${formatList(unsupportedCardIds)} are of card types that are currently not supported.`
            : '';

      const body = cardTexts.length > 0 ? cardTexts.join('\n\n') : '';
      const messages = [body, missingNodesMessage, unsupportedMessage].filter(Boolean);

      return toolResult(messages.join('\n\n'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      return toolResult(`Error retrieving card details: ${errorMessage}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.GetCardDetailsFromRedux,
    displayName: 'Retrieving Card Details',
    description: 'Retrieves full details of cards.',
    execute,
    inputSchema: getCardDetailsFromReduxToolSchema,
  });
}
